import { useEffect, useState } from 'react';
import { getRepertoireForSong } from '../../../services/RepertoireService';
import { fetchTracksForStudent } from '../../../services/StudentTracksService';

/**
 * For a recital's set list: each song's repertoire entries (where the recital
 * track is marked) and each performer's track pointers (where the files are).
 * One query per song and one per performer: a program is ~20 songs and a few
 * dozen performers. Fails quiet: a teacher without `viewStudents` can read
 * this page but not those collections, and gets the page without the lines.
 */
const useRecitalTracks = (setList = []) => {
	const [state, setState] = useState({ loading: false, repBySong: {}, docsByStudent: {}, available: true });
	const songKey = setList.map((e) => e.canonicalSongId).join('|');
	const performerKey = [...new Set(setList.flatMap((e) => e.performers || []))].sort().join('|');

	useEffect(() => {
		let cancelled = false;
		const songIds = songKey ? songKey.split('|') : [];
		const studentIds = performerKey ? performerKey.split('|') : [];
		if (!songIds.length) {
			setState({ loading: false, repBySong: {}, docsByStudent: {}, available: true });
			return undefined;
		}
		setState((s) => ({ ...s, loading: true }));
		(async () => {
			try {
				const [reps, docs] = await Promise.all([
					Promise.all(songIds.map((id) => getRepertoireForSong(id))),
					Promise.all(studentIds.map((id) => fetchTracksForStudent(id))),
				]);
				if (cancelled) return;
				const repBySong = {};
				songIds.forEach((id, i) => {
					repBySong[id] = reps[i];
				});
				const docsByStudent = {};
				studentIds.forEach((id, i) => {
					docsByStudent[id] = docs[i];
				});
				setState({ loading: false, repBySong, docsByStudent, available: true });
			} catch (e) {
				console.error('Recital tracks could not be loaded:', e);
				if (!cancelled) setState({ loading: false, repBySong: {}, docsByStudent: {}, available: false });
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [songKey, performerKey]);

	return state;
};

export default useRecitalTracks;
